import { Chip, ChipProps, Typography } from '@mui/material';
import { AccommodationSpecificity, SpecificityObject } from '@silbo/core';
import { getSpecificityIconComponent } from '@silbo/ui';
import { useTranslation } from 'react-i18next';
import { colorUtils } from '../utils/color-utils';
import { specificityColor, specificityIcon } from './SpecificityChipConfig';

const TEXT_COLOR_BRIGHTNESS_FACTOR = 0.7;

type SpecificityChipProps = NativeSpecificityChipProps | CustomSpecificityChipProps;

const SpecificityChip: React.FC<SpecificityChipProps> = ({ specificity, ...props }) =>
  typeof specificity === 'string' ? (
    <NativeSpecificityChip specificity={specificity} {...props} />
  ) : (
    <CustomSpecificityChip specificity={specificity} {...props} />
  );

type NativeSpecificityChipProps = ChipProps & {
  specificity: AccommodationSpecificity;
};

const NativeSpecificityChip: React.FC<NativeSpecificityChipProps> = ({ specificity, onDelete, disabled, ...rest }) => {
  const { t } = useTranslation('bed-management');
  return (
    <Chip
      color={specificityColor[specificity]}
      label={t(`accommodationSpecificities.${specificity}`)}
      onDelete={onDelete}
      disabled={disabled}
      icon={specificityIcon[specificity]}
      variant="roundedAlt"
      data-testid={`specificityChip.${specificity}`}
      sx={{
        '.MuiSvgIcon-root': {
          overflow: 'visible',
        },
        ...rest.sx,
      }}
    />
  );
};

type CustomSpecificityChipProps = ChipProps & {
  specificity: SpecificityObject;
};

const CustomSpecificityChip: React.FC<CustomSpecificityChipProps> = ({ specificity, onDelete, disabled, ...rest }) => {
  const Icon = getSpecificityIconComponent(specificity.icon);

  return (
    <Chip
      label={specificity.name}
      onDelete={onDelete}
      disabled={disabled}
      icon={
        Icon ? (
          <div style={{ backgroundColor: specificity.color }}>
            <Icon sx={{ fontSize: 16 }} />
          </div>
        ) : (
          <Typography variant="body2" fontWeight={600} bgcolor={`${specificity.color} !important`} alignItems="center">
            {specificity.icon}
          </Typography>
        )
      }
      variant="roundedAlt"
      {...rest}
      data-testid={`specificityChip.${specificity.name}`}
      sx={{
        color: getTextColor(specificity.color),
        ...rest.sx,
      }}
    />
  );
};

const getTextColor = (color: string): string => {
  const hsvColor = colorUtils.hexToHsv(color);
  return colorUtils.hsvToHex(hsvColor.h, hsvColor.s, hsvColor.v * TEXT_COLOR_BRIGHTNESS_FACTOR);
};

export default SpecificityChip;
